/**
 * THE STAGE — the one coordinate system the server and every client agree on.
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 * Everyone in a saloon shoots the same deer. If a hit were resolved in screen
 * pixels, a player on a 6.9" phone and a player on a tablet would be shooting at
 * different things, and "I hit it" would depend on hardware. So the wire format
 * for a shot is a point in *stage* coordinates, and the server's hit test runs
 * in stage coordinates. Pixels exist only inside the renderer and inside the
 * aim controller, and they are converted at the boundary.
 *
 *   sx  −1 … +1   left … right
 *   sy  −1 … +1   top  … bottom
 *
 * The stage has a FIXED aspect ratio (3:4, portrait). It is fitted into the
 * viewport with `contain`, so the entire stage — and therefore every deer — is
 * visible to every player regardless of their screen. Backgrounds are drawn
 * beyond the stage to fill the viewport, but nothing that can be shot ever
 * lives out there. Fairness is the reason the fit is `contain` and not `cover`.
 *
 * Portrait, because the phone is the gun. You point a phone at something the
 * way you point a torch at it: upright, in one hand. A landscape stage would be
 * a better shape for a scene and a worse shape for the actual gesture, and the
 * gesture is the product.
 *
 * ── Depth ───────────────────────────────────────────────────────────────────
 * `z` runs 0 (at your boots) to 1 (the treeline). It is not a distance in
 * metres; it is the parameter of a perspective curve chosen so that the deer
 * look right, which is the only requirement a hunting game has.
 */

/** Stage width ÷ stage height. Portrait. */
export const STAGE_ASPECT = 3 / 4;

/** Where the treeline sits, in stage `sy`. Everything above is sky. */
export const HORIZON_SY = -0.18;

/** Where a deer standing at z = 0 has its hooves. */
export const FOREGROUND_SY = 0.86;

/**
 * Perspective exponent for the ground plane. 1.0 would be a linear ramp from
 * the horizon to the foreground, which reads as a flat wall; >1 compresses
 * distance near the horizon the way an actual ground plane does.
 */
const GROUND_POWER = 1.7;

/** Scale of a deer at z = 0, as a fraction of stage half-height. */
const NEAR_SCALE = 0.52;

/**
 * Depth falloff for apparent size. `scale = NEAR_SCALE / (1 + z * DEPTH_K)`,
 * the reciprocal falloff a real lens produces, rather than a lerp — a lerp
 * makes distant deer far too big and kills the sense of a long shot.
 */
const DEPTH_K = 3.6;

/** Deer are culled once they pass this far outside the stage. */
export const EXIT_SX = 1.28;

/** Where a deer standing at depth `z` has its hooves, in stage `sy`. */
export function groundSy(z: number): number {
  const k = Math.pow(1 - clamp01(z), GROUND_POWER);
  return HORIZON_SY + (FOREGROUND_SY - HORIZON_SY) * k;
}

/** Apparent size multiplier for something at depth `z`. */
export function depthScale(z: number): number {
  return NEAR_SCALE / (1 + clamp01(z) * DEPTH_K);
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * The stage rectangle inside a viewport, in CSS pixels. `contain`, centred
 * horizontally, and biased UP vertically — the HUD lives along the bottom edge
 * and a centred stage would put the foreground deer behind the shell counter.
 */
export interface StageFit {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function fitStage(viewW: number, viewH: number): StageFit {
  const h = Math.min(viewH, viewW / STAGE_ASPECT);
  const w = h * STAGE_ASPECT;
  return {
    x: (viewW - w) / 2,
    // 0.5 would centre it. 0.38 lifts the scene so the bottom of the stage sits
    // above the thumb zone, where the reload control and the shell counter are.
    y: (viewH - h) * 0.38,
    w,
    h,
  };
}

export function stageToPx(fit: StageFit, sx: number, sy: number): { x: number; y: number } {
  return { x: fit.x + ((sx + 1) / 2) * fit.w, y: fit.y + ((sy + 1) / 2) * fit.h };
}

export function pxToStage(fit: StageFit, x: number, y: number): { sx: number; sy: number } {
  return { sx: ((x - fit.x) / fit.w) * 2 - 1, sy: ((y - fit.y) / fit.h) * 2 - 1 };
}
